import type {
  AppliedBundle,
  BundleCard,
  BundleHintView,
  BundleSlotKind,
  LocalizedText,
  OrderBundlesView,
  SelectedBundleView,
} from "src/services/bundle-api";
import { MAP_KIND, mapPageHref } from "src/components/map/map-links";

const isCents = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

/** A sentence to translate, never a translated string: the components only translate. */
export type CopyLine = { key: string; params: Record<string, string> };

export function localizedName(
  text: LocalizedText | null | undefined,
  locale: string,
): string {
  if (!text) return "";
  const lang = locale.split("-")[0];
  // sl and si are one language (photo-size names use si).
  const alias = lang === "sl" ? "si" : lang === "si" ? "sl" : "";
  return (
    text[locale] ||
    text[lang] ||
    (alias && text[alias]) ||
    text.sl ||
    text.si ||
    text.en ||
    Object.values(text).find(Boolean) ||
    ""
  );
}

type SlotLike = {
  kind: BundleSlotKind;
  photoSizeId: string | null;
  variantIds: string[];
  productIds: string[];
  definitionId: string | null;
  definitionKind: string | null;
  slotId?: string;
};

/**
 * Where a slot row leads (plan decision 13). With `bundleId` the link carries
 * `bundle` (and `slot`) so the target page can say what it is for (the paket
 * context strip) and the home page knows the visit is in-session paket
 * navigation, not a returning customer to offer the unfinished order to.
 */
export function slotHref(
  slot: SlotLike,
  locale: string,
  bundleId?: string | null,
): string {
  const params: Array<[string, string]> = [];
  let path: string;
  let hash = "";
  if (slot.kind === "PHOTO_SIZE") {
    path = `/${locale}`;
    params.push(["photoSizeId", slot.photoSizeId ?? ""]);
  } else if (slot.kind === "PWM_VARIANT") {
    if (slot.definitionId && slot.definitionKind === "canvas") {
      path = `/${locale}/canvas/${encodeURIComponent(slot.definitionId)}`;
      params.push(["variant", slot.variantIds[0] ?? ""]);
    } else if (slot.definitionKind === MAP_KIND) {
      // The map product has its own page; /design/<id> is the greeting-card
      // editor and cannot open it (src/components/map/map-links.ts).
      path = mapPageHref(locale);
    } else if (slot.definitionId) {
      path = `/${locale}/design/${encodeURIComponent(slot.definitionId)}`;
    } else {
      path = `/${locale}/produkti`;
    }
  } else {
    path = `/${locale}`;
    params.push(["product", slot.productIds[0] ?? ""]);
    hash = "#izdelki";
  }
  if (bundleId) {
    params.push(["bundle", bundleId]);
    if (slot.slotId) params.push(["slot", slot.slotId]);
  }
  const query = params
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  return `${path}${query ? `?${query}` : ""}${hash}`;
}

/**
 * The paket context strip on a slot's target page ("Za paket »Spomini«" and
 * "1 × Platno 60×60"): only when the link's `bundle` is the selected or an
 * applied bundle. The slot line is omitted when the slot is unknown.
 */
export function bundleContext(input: {
  bundleId: string | null | undefined;
  slotId: string | null | undefined;
  view: OrderBundlesView | null | undefined;
  applied: AppliedBundle[];
  cards?: BundleCard[] | null;
  locale: string;
}): { name: string; slot: { quantity: number; label: string } | null } | null {
  const { bundleId, slotId, view, locale } = input;
  if (!bundleId) return null;
  const selected =
    view?.selected?.bundleId === bundleId ? view.selected : null;
  const applied = input.applied.find((a) => a.bundleId === bundleId);
  if (!selected && !applied) return null;
  const card = input.cards?.find((c) => c.id === bundleId);
  const name = selected
    ? selected.name
    : localizedName(applied!.name, locale) || card?.name || "";
  let slot: { quantity: number; label: string } | null = null;
  if (slotId) {
    const s = selected?.slots.find((x) => x.slotId === slotId);
    if (s) slot = { quantity: s.need, label: s.label };
    else {
      const c = card?.slots.find((x) => x.id === slotId);
      if (c) slot = { quantity: c.quantity, label: c.label };
    }
  }
  return { name, slot };
}

/**
 * A slot row's status. The server decides what the row shows (`count` and
 * `full`, order-bundles-view.ts displayProgress): full once the bundle has
 * applied, until units toward the next one exist. `have` is the fallback for
 * an API that does not send `count` yet.
 */
export function slotStatus(slot: {
  kind: BundleSlotKind;
  have: number;
  count?: number;
  need: number;
  full: boolean;
}): CopyLine {
  const count = slot.count ?? slot.have;
  if (slot.full) return { key: "bundles.slotFull", params: {} };
  if (slot.need === 1 && count === 0 && slot.kind === "PWM_VARIANT")
    return { key: "bundles.slotChoosePhoto", params: {} };
  if (slot.need === 1 && count === 0 && slot.kind === "PRODUCT")
    return { key: "bundles.slotChooseProduct", params: {} };
  return {
    key: "bundles.slotProgress",
    params: { have: String(count), need: String(slot.need) },
  };
}

function appliedCopy(
  selected: SelectedBundleView,
  fmt: (cents: number) => string,
): CopyLine | null {
  if (selected.timesApplied > 1)
    return {
      key: "bundles.panelCompleteTimes",
      params: {
        times: String(selected.timesApplied),
        saving: fmt(selected.appliedDiscountCents),
      },
    };
  if (selected.timesApplied === 1)
    return {
      key: "bundles.panelComplete",
      params: { saving: fmt(selected.appliedDiscountCents) },
    };
  return null;
}

export function panelHeaderCopy(
  selected: SelectedBundleView,
  fmt: (cents: number) => string,
): CopyLine {
  // The card's catalogue figures, the ones the customer chose by: the
  // server's normalCents (at the cart's rung) would make one paket show two
  // normal prices (63,90 € on the card, 62,90 € here). No saving is stated
  // in this header, so nothing under it can contradict it.
  const normalCents = selected.normalPriceCents;
  // The rows count toward the next application: say which one it is.
  if (selected.towardNext)
    return {
      key: "bundles.panelNext",
      params: {
        next: String(selected.timesApplied + 1),
        name: selected.name,
        price: fmt(selected.priceCents),
        normal: fmt(normalCents),
      },
    };
  const applied = appliedCopy(selected, fmt);
  if (applied) return applied;
  return {
    key: "bundles.panelHeader",
    params: {
      name: selected.name,
      price: fmt(selected.priceCents),
      normal: fmt(normalCents),
    },
  };
}

/** The paket's row by the total: "Prihranek s paketom »Spomini«" (×n). */
export function appliedLineCopy(
  applied: AppliedBundle,
  locale: string,
): CopyLine {
  const name = localizedName(applied.name, locale);
  return applied.times > 1
    ? {
        key: "bundles.totalLineTimes",
        params: { name, times: String(applied.times) },
      }
    : { key: "bundles.totalLine", params: { name } };
}

/**
 * The hint card's title, before anything has applied: which paket, what it
 * costs and its normal price, from its public card, the figures the customer
 * sees on /paketi. Without a card, the server's figures (price = normal −
 * saving). Just the name when neither is known.
 */
export function hintTitleCopy(
  hint: { bundleId: string; name: string; savingCents?: number; normalCents?: number },
  cards: BundleCard[] | null | undefined,
  fmt: (cents: number) => string,
): CopyLine {
  const card = cards?.find((c) => c.id === hint.bundleId);
  const figures = card
    ? { priceCents: card.priceCents, normalCents: card.normalPriceCents }
    : bundleFigures(
        hint.bundleId,
        { savingCents: hint.savingCents ?? 0, normalCents: hint.normalCents },
        null,
      );
  return figures.normalCents !== null && figures.priceCents !== null
    ? {
        key: "bundles.hintTitle",
        params: {
          name: hint.name,
          price: fmt(figures.priceCents),
          normal: fmt(figures.normalCents),
        },
      }
    : { key: "bundles.line", params: { name: hint.name } };
}

/**
 * The hint line, with the server's real saving for this cart. Under a title
 * with the card's figures ("49,00 € namesto 63,90 €"), a different saving
 * says what it is: "pri vaši košarici prihranite 7,90 €" (bundles.hintCart),
 * so the block never reads as two contradicting savings. The card's own
 * saving, or no card: the plain line.
 */
export function hintCopy(
  hint: BundleHintView,
  fmt: (cents: number) => string,
  joiner: string,
  cards?: BundleCard[] | null,
): CopyLine {
  const card = cards?.find((c) => c.id === hint.bundleId);
  const differs =
    !!card && card.normalPriceCents - card.priceCents !== hint.savingCents;
  return {
    key: differs ? "bundles.hintCart" : "bundles.hint",
    params: {
      missing: hint.missing.map((m) => `${m.units} × ${m.label}`).join(joiner),
      saving: fmt(hint.savingCents),
    },
  };
}

/** The bundle that blocks the order's coupon right now, by name, from the order itself. */
export function couponBlockedName(
  order:
    | { couponUsed?: unknown; appliedBundles?: AppliedBundle[] | null }
    | null
    | undefined,
  locale: string,
): string | null {
  if (!order?.couponUsed) return null;
  const blocking = (order.appliedBundles ?? []).find((b) => !b.couponsAllowed);
  return blocking ? localizedName(blocking.name, locale) : null;
}

/**
 * The coupon code a confirmation or receipt lists as used: none while an
 * applied bundle blocks it, because it gave nothing (pricing nulls it).
 */
export function couponCodeAsUsed(order: unknown): string | null {
  const o = (order ?? {}) as { couponUsed?: { code?: string | null } | null };
  const code = o.couponUsed?.code ?? null;
  if (!code) return null;
  const blocked = orderBundleFields(order).appliedBundles.some(
    (b) => !b.couponsAllowed,
  );
  return blocked ? null : code;
}

/** The bundle columns, read off a generated GetOrderDto that does not declare them. */
export function orderBundleFields(order: unknown): {
  selectedBundleId: string | null;
  appliedBundles: AppliedBundle[];
  bundleDiscountCents: number;
} {
  const o = (order ?? {}) as {
    selectedBundleId?: string | null;
    appliedBundles?: AppliedBundle[] | null;
    bundleDiscountCents?: unknown;
  };
  return {
    selectedBundleId: o.selectedBundleId ?? null,
    appliedBundles: Array.isArray(o.appliedBundles) ? o.appliedBundles : [],
    bundleDiscountCents: Math.round(Number(o.bundleDiscountCents) || 0),
  };
}

/**
 * The coupon as the server prices it: none while an applied bundle with
 * "Kuponi dovoljeni" off is on the order (pricing.service nulls it entirely,
 * so no free shipping and no COD-fee waiver either). Use it wherever the
 * client derives shipping or fees from the coupon, so the page never quotes
 * less than the charge.
 */
export function couponAsPriced<C>(
  coupon: C | null | undefined,
  order: unknown,
): C | null {
  if (!coupon) return null;
  // A cashback coupon discounts nothing: never free shipping, never the
  // 100 % promo branch (cashback-coupon spec D12).
  if ((coupon as { rewardType?: unknown }).rewardType === "CASHBACK")
    return null;
  const blocked = orderBundleFields(order).appliedBundles.some(
    (b) => !b.couponsAllowed,
  );
  return blocked ? null : coupon;
}

/**
 * A valid 100 % free-shipping PERCENTAGE coupon waives the cash-on-delivery
 * fee, judged through couponAsPriced: a CASHBACK coupon or one a bundle blocks
 * waives nothing (cashback-coupon spec D12).
 */
export function couponWaivesCodFee(coupon: unknown, order: unknown): boolean {
  const c = couponAsPriced(
    coupon as {
      type?: string;
      discount?: number;
      freeShipping?: boolean;
      validUntil?: string | Date | null;
    } | null,
    order,
  );
  return (
    !!c &&
    c.type === "PERCENTAGE" &&
    !!c.freeShipping &&
    Number(c.discount) === 100 &&
    new Date(c.validUntil ?? 0) >= new Date()
  );
}

// ── The cart's paket block (cart-design brief, 2026-10-05) ──────────────────
//
// One block per bundle, in exactly one of two states: FILLING (selected, not
// applied yet: header, slot rows, "Odstrani paket") or APPLIED (a
// confirmation with the saving, and at most one quiet line toward the next
// application). Slot rows never headline an applied bundle.

/** What one more application of an applied bundle still needs. */
export type BundleNext = {
  bundleId: string;
  /** The application it would be: 2 for "2. paket". */
  next: number;
  savingCents: number;
  /** Units missing over every slot: the divisor of the hint-winner rule. */
  units: number;
  missing: Array<{ label: string; units: number }>;
};

/**
 * Why a FILLING paket's rows stay short although the cart holds their items:
 * another applied paket took them (paket deep walk N3).
 */
export type TakenBy = {
  /** The other applied pakets, by name. */
  names: string[];
  /** Every short slot is a photo slot: "Fotografije …", else "Izdelki …". */
  photosOnly: boolean;
};

export type BundleBlock =
  | {
      state: "filling";
      bundleId: string;
      selected: SelectedBundleView;
      takenBy: TakenBy | null;
    }
  | {
      state: "applied";
      bundleId: string;
      name: string;
      times: number;
      discountCents: number;
      /** "100 × 10×15 sijaj · 1 × Platno 60×60"; empty when unknown. */
      contents: Array<{ quantity: number; label: string }>;
      next: BundleNext | null;
    };

/** A saving and the units it takes to get it, for the hint-winner rule. */
export type HintRate = { savingCents: number; units: number };

export type BundlePanelModel = {
  blocks: BundleBlock[];
  /** The server's hint for a bundle with no block of its own: a card of its own. */
  hint: BundleHintView | null;
  /** The best bundle-side hint, against which the ladder hint is weighed. */
  bundleHint: HintRate | null;
};

const rate = (h: HintRate) => h.savingCents / h.units;

function bestRate(rates: Array<HintRate | null>): HintRate | null {
  let best: HintRate | null = null;
  for (const r of rates)
    if (r && r.units > 0 && r.savingCents > 0 && (!best || rate(r) > rate(best)))
      best = { savingCents: r.savingCents, units: r.units };
  return best;
}

/**
 * What the next application of a paket saves, and the normal price it is
 * measured against, so "49,00 € namesto Y" and "prihranite Z" always agree
 * (Y − 49,00 = Z).
 *
 * The server's figures come first: it prices the saving at the ladder rung
 * the cart will be on once the paket applies (paket deep walk N2: the card's
 * figure, the catalogue ladder at the slot's quantity, promised −13,90 € in a
 * 300-photo cart where the paket really saved 7,90 €). An older API sends no
 * `normalCents`, and its saving is not trustworthy (it once valued the units
 * already in the cart at their current rung: 17,30 € against 14,90 €), so
 * there the public card's figures stand in. Without a card either, the
 * server's saving is all there is.
 */
export function bundleFigures(
  bundleId: string,
  server: { savingCents: number; normalCents?: number | null },
  cards: BundleCard[] | null | undefined,
): { savingCents: number; normalCents: number | null; priceCents: number | null } {
  if (isCents(server.normalCents)) {
    const savingCents = Math.max(0, server.savingCents);
    return {
      savingCents,
      normalCents: server.normalCents,
      priceCents: server.normalCents - savingCents,
    };
  }
  const card = cards?.find((c) => c.id === bundleId);
  return card
    ? {
        savingCents: Math.max(0, card.normalPriceCents - card.priceCents),
        normalCents: card.normalPriceCents,
        priceCents: card.priceCents,
      }
    : { savingCents: server.savingCents, normalCents: null, priceCents: null };
}

function nextFromSelected(selected: SelectedBundleView): BundleNext | null {
  if (!selected.repeatable || !selected.towardNext) return null;
  const missing = selected.slots
    .map((s) => ({ label: s.label, units: Math.max(0, s.need - s.have) }))
    .filter((m) => m.units > 0);
  const units = missing.reduce((n, m) => n + m.units, 0);
  if (units === 0 || selected.nextSavingCents <= 0) return null;
  return {
    bundleId: selected.bundleId,
    next: selected.timesApplied + 1,
    savingCents: selected.nextSavingCents,
    units,
    missing,
  };
}

function nextFromHint(hint: BundleHintView, times: number): BundleNext | null {
  const missing = hint.missing
    .filter((m) => m.units > 0)
    .map((m) => ({ label: m.label, units: m.units }));
  const units = missing.reduce((n, m) => n + m.units, 0);
  if (units === 0 || hint.savingCents <= 0) return null;
  return {
    bundleId: hint.bundleId,
    next: times + 1,
    savingCents: hint.savingCents,
    units,
    missing,
  };
}

/**
 * The blocks, from the order's `appliedBundles` (the source the total is
 * priced with, so an auto-matched bundle gets its APPLIED block too) and the
 * `/bundles` view (the selection, its slots and the hint). `view` is null
 * while the switch is off or the request failed: the applied blocks still show.
 */
export function bundlePanelModel(input: {
  applied: AppliedBundle[];
  view: OrderBundlesView | null | undefined;
  cards?: BundleCard[] | null;
  locale: string;
}): BundlePanelModel {
  const { view, locale } = input;
  const selected = view?.selected ?? null;
  const rawHint = view?.hint ?? null;
  const serverHint: BundleHintView | null = rawHint
    ? {
        ...rawHint,
        savingCents: bundleFigures(rawHint.bundleId, rawHint, input.cards)
          .savingCents,
      }
    : null;
  const applied = [...input.applied];
  // The view can know of an application the cached order has not caught up with.
  if (
    selected &&
    selected.timesApplied >= 1 &&
    !applied.some((a) => a.bundleId === selected.bundleId)
  )
    applied.push({
      bundleId: selected.bundleId,
      name: { [locale]: selected.name },
      times: selected.timesApplied,
      discountCents: selected.appliedDiscountCents,
      couponsAllowed: true,
    });

  const contentsOf = (bundleId: string) => {
    if (selected?.bundleId === bundleId)
      return selected.slots.map((s) => ({ quantity: s.need, label: s.label }));
    const card = input.cards?.find((c) => c.id === bundleId);
    return card
      ? card.slots.map((s) => ({ quantity: s.quantity, label: s.label }))
      : [];
  };

  const blocks: BundleBlock[] = applied.map((a) => {
    const fromSelected =
      selected?.bundleId === a.bundleId
        ? nextFromSelected({
            ...selected,
            nextSavingCents: bundleFigures(
              selected.bundleId,
              {
                savingCents: selected.nextSavingCents,
                normalCents: selected.normalCents,
              },
              input.cards,
            ).savingCents,
          })
        : null;
    const fromHint =
      !fromSelected && serverHint?.bundleId === a.bundleId
        ? nextFromHint(serverHint, a.times)
        : null;
    return {
      state: "applied",
      bundleId: a.bundleId,
      name: localizedName(a.name, locale),
      times: a.times,
      discountCents: a.discountCents,
      contents: contentsOf(a.bundleId),
      next: fromSelected ?? fromHint,
    };
  });
  if (selected && selected.timesApplied === 0)
    blocks.push({
      state: "filling",
      bundleId: selected.bundleId,
      selected,
      takenBy: takenByOthers(selected, applied, locale),
    });

  // The hint for a bundle that already has a block is that block's business.
  const hint =
    serverHint && !blocks.some((b) => b.bundleId === serverHint.bundleId)
      ? serverHint
      : null;
  const hintNext = hint ? nextFromHint(hint, 0) : null;
  return {
    blocks,
    hint,
    bundleHint: bestRate([
      ...blocks.map((b) => (b.state === "applied" ? b.next : null)),
      hintNext,
    ]),
  };
}

/**
 * The selected paket has not applied, yet the cart holds its items: another
 * applied paket consumed them. The server's `targetUnits` is the cart count
 * at which this slot would be full, i.e. the units every application already
 * took plus `need`; with no application of its own, the excess over `need` is
 * what the other pakets took. Null when nothing was taken from a short slot
 * or no other paket is known by name.
 */
export function takenByOthers(
  selected: SelectedBundleView,
  applied: AppliedBundle[],
  locale: string,
): TakenBy | null {
  if (selected.timesApplied !== 0) return null;
  const short = selected.slots.filter(
    (s) => !s.full && (Number(s.targetUnits) || 0) - s.need > 0,
  );
  if (short.length === 0) return null;
  const names = applied
    .filter((a) => a.bundleId !== selected.bundleId && a.times > 0)
    .map((a) => localizedName(a.name, locale))
    .filter(Boolean);
  if (names.length === 0) return null;
  return {
    names: [...new Set(names)],
    photosOnly: short.every((s) => s.kind === "PHOTO_SIZE"),
  };
}

export function takenByCopy(taken: TakenBy): CopyLine {
  return {
    key: taken.photosOnly ? "bundles.takenByPhotos" : "bundles.takenByItems",
    // Rarely more than one; the key's own quotes wrap the list.
    params: { name: taken.names.join(", ") },
  };
}

/**
 * The ladder hint ("Dodaj še N fotografij in prihrani") as a saving: the
 * per-photo drop, after the coupon, over every photo at the next tier.
 */
export function ladderHintRate(
  ladder:
    | {
        missing: number;
        nextAt: number;
        effectivePricePerPhoto: number;
        effectiveNextPricePerPhoto: number;
      }
    | null
    | undefined,
): HintRate | null {
  if (!ladder || ladder.missing <= 0) return null;
  const savingCents = Math.round(
    (ladder.effectivePricePerPhoto - ladder.effectiveNextPricePerPhoto) *
      ladder.nextAt,
  );
  return savingCents > 0 ? { savingCents, units: ladder.missing } : null;
}

/**
 * One hint at a time: the larger saving per added item wins, and the other
 * shows only when the winner is absent. A tie goes to the bundle.
 */
export function pickHint(
  ladder: HintRate | null,
  bundle: HintRate | null,
): "ladder" | "bundle" | null {
  const l = bestRate([ladder]);
  const b = bestRate([bundle]);
  if (l && b) return rate(l) > rate(b) ? "ladder" : "bundle";
  return l ? "ladder" : b ? "bundle" : null;
}

export function appliedHeaderCopy(block: {
  name: string;
  times: number;
}): CopyLine {
  return block.times > 1
    ? {
        key: "bundles.appliedHeaderTimes",
        params: { times: String(block.times), name: block.name },
      }
    : { key: "bundles.appliedHeader", params: { name: block.name } };
}

export function nextLineCopy(
  next: BundleNext,
  fmt: (cents: number) => string,
  joiner: string,
): CopyLine {
  return {
    key: "bundles.nextLine",
    params: {
      missing: next.missing.map((m) => `${m.units} × ${m.label}`).join(joiner),
      next: String(next.next),
      saving: `−${fmt(next.savingCents)}`,
    },
  };
}

/**
 * The pakets that expired under this cart (ended or switched off since it was
 * last priced), from every place the server may say so: the bundles view, the
 * order, a calculate-amount response. `bundleExpired: [{ bundleId?, name }]`,
 * the name plain or localized. One entry per paket, keyed by id (or name),
 * the order of first mention kept. Absent everywhere on an older API.
 */
export function expiredBundles(
  sources: unknown[],
  locale: string,
): Array<{ key: string; name: string }> {
  const out = new Map<string, string>();
  for (const src of sources) {
    const list = (src as { bundleExpired?: unknown } | null | undefined)
      ?.bundleExpired;
    if (!Array.isArray(list)) continue;
    for (const raw of list) {
      const e = raw as { bundleId?: unknown; name?: unknown } | null;
      if (!e) continue;
      const name =
        typeof e.name === "string"
          ? e.name
          : e.name && typeof e.name === "object"
            ? localizedName(e.name as LocalizedText, locale)
            : "";
      if (!name) continue;
      const key = typeof e.bundleId === "string" && e.bundleId ? e.bundleId : name;
      if (!out.has(key)) out.set(key, name);
    }
  }
  return [...out].map(([key, name]) => ({ key, name }));
}

/**
 * The server refused to place the order (COD confirm, or the card payment
 * session) because a paket expired since the cart was priced: 409 with code
 * BUNDLE_EXPIRED and `{ bundleExpired: [{ bundleId, name }], amount }`, the
 * amount being the order's new total in cents. Null for any other error, so
 * the page's own handling stays. `data` is the payload, to be noted with
 * rememberBundleExpired.
 */
export function bundleExpiredConflict(error: unknown): {
  data: { bundleExpired: unknown[] };
  amountCents: number | null;
} | null {
  const res = (error as {
    response?: { status?: number; data?: Record<string, unknown> | null };
  } | null)?.response;
  const data = res?.data ?? null;
  if (!data) return null;
  const status = res?.status ?? Number(data.statusCode);
  const code = data.code ?? data.message ?? data.error;
  if (status !== 409 || code !== "BUNDLE_EXPIRED") return null;
  // The payload may sit at the top level or under `payload`/`details`.
  const inner = [data, data.payload, data.details].find(
    (d) =>
      d &&
      typeof d === "object" &&
      Array.isArray((d as { bundleExpired?: unknown }).bundleExpired),
  ) as { bundleExpired: unknown[]; amount?: unknown } | undefined;
  const rawAmount = inner?.amount ?? data.amount;
  const amount = Number(rawAmount);
  return {
    data: { bundleExpired: inner?.bundleExpired ?? [] },
    amountCents:
      rawAmount != null && rawAmount !== "" && Number.isFinite(amount)
        ? Math.round(amount)
        : null,
  };
}

/**
 * On Pay by card, the shipping update reprices the order and stores it
 * before the payment call. If that reprice dropped a paket that ended while
 * the customer was on the delivery step, the server reports `bundleExpired`
 * on the returned order, and the payment call that follows has nothing left
 * to refuse: the customer would land on Stripe at the higher price with no
 * word from us (final review B, I1). Then stop: the notice and the new total
 * show, and the next press pays the stored price. Null: carry on. COD is not
 * stopped here: its confirm page shows the notice from the order and the
 * server refuses there with 409.
 */
export function expiredBeforePayment(
  order: unknown,
  isCard: boolean,
): { data: { bundleExpired: unknown[] }; amountCents: number | null } | null {
  if (!isCard) return null;
  const o = (order ?? {}) as { bundleExpired?: unknown; amount?: unknown };
  if (!Array.isArray(o.bundleExpired) || o.bundleExpired.length === 0)
    return null;
  const amount = Number(o.amount);
  return {
    data: { bundleExpired: o.bundleExpired },
    amountCents: o.amount != null && Number.isFinite(amount) ? Math.round(amount) : null,
  };
}

/**
 * The "Nov znesek naročila" figure after an expiry stop or refusal: the
 * frozen figure while the order still shows the amount it had before (the
 * refetch has not landed), and the order's current amount once it changed,
 * so a later coupon or option change never leaves a stale figure. A 0 total
 * (fully credited) is a real total.
 */
export function expiredNewTotalCents(
  refusal: { amountCents: number | null; before: number | null } | null | undefined,
  currentAmount: unknown,
): number | null {
  if (!refusal) return null;
  const current =
    currentAmount != null && Number.isFinite(Number(currentAmount))
      ? Math.round(Number(currentAmount))
      : null;
  if (current !== null && current !== refusal.before) return current;
  return refusal.amountCents ?? current;
}

/**
 * A cart line the applied bundles cover: it gets the "V paketu" tag. Takes
 * any line: the generated DTOs do not declare `bundleDiscount` (a decimal
 * column, so it arrives as a string).
 */
export function inBundle(line: unknown): boolean {
  const l = line as { bundleDiscount?: unknown } | null | undefined;
  return (Number(l?.bundleDiscount) || 0) > 0;
}
